/**
 * The sticky action bar at the bottom of the score page. Three states,
 * derived from flight-completeness flags + the play sequence:
 *
 *   1. all 18 holes scored across the flight → "Lever scorekortet"
 *      (terminal commit; red shadow)
 *   2. current hole missing scores           → disabled hint
 *      "Fyll ut alle slag i flighten"
 *   3. otherwise                             → "Bekreft & gå til hull X"
 *      where X = nextInSequence(startHole, currentHole). On the last
 *      hole in the rotation the label drops to plain "Bekreft".
 *
 * Submit opens the bottom-sheet confirm sheet (parent owns the state).
 * Advance updates the URL `?hole=N` (parent's `onAdvance` callback).
 */
const ActionBar = ({
  allHolesScored,
  allScoredCurrentHole,
  submitting,
  nextHole,
  onSubmit,
  onAdvance,
}: {
  allHolesScored: boolean
  allScoredCurrentHole: boolean
  submitting: boolean
  nextHole: number | null
  onSubmit: () => void
  onAdvance: (hole: number) => void
}) => {
  const canSubmit = allHolesScored && !submitting
  const canAdvance = !canSubmit && allScoredCurrentHole && !submitting
  const enabled = canSubmit || canAdvance

  const label = canSubmit
    ? submitting
      ? 'Leverer…'
      : 'Lever scorekortet'
    : !allScoredCurrentHole
    ? 'Fyll ut alle slag i flighten'
    : nextHole != null
    ? `Bekreft og gå til hull ${nextHole}`
    : 'Bekreft'

  const onClick = () => {
    if (canSubmit) {
      onSubmit()
      return
    }
    if (canAdvance && nextHole != null) {
      onAdvance(nextHole)
    }
  }

  return (
    <div className="px-4 py-4 sticky bottom-bottom-nav z-10">
      <button
        type="button"
        disabled={!enabled}
        onClick={onClick}
        className={`w-full py-3.5 rounded-xl font-display font-extrabold text-base transition-colors ${
          !enabled
            ? 'bg-ink-100 text-ink-500 cursor-not-allowed'
            : canSubmit
            ? 'bg-flag text-paper hover:bg-flag-dark'
            : 'bg-green-700 text-paper hover:bg-green-800'
        }`}
        style={
          enabled
            ? {
                // Red shadow on the terminal "Lever" commit, green on
                // the step-through advance — design.md §4 + §6.5.
                boxShadow: canSubmit
                  ? '0 8px 20px -8px rgba(168, 37, 20, 0.6)'
                  : '0 8px 20px -8px rgba(11, 42, 28, 0.6)',
              }
            : undefined
        }
      >
        {label}
      </button>
    </div>
  )
}

export default ActionBar
